import React from "react";
import AssetsIcon from "../../Assets/AssetsIcon";
import {
  formatAssetKindOptionLabel,
  getAssetIconValueLabels,
  loadAssetIconOptions,
} from "../../Assets/assetIconsList";
import BoomIcon from "../../Common/BoomIcon";
import {
  formatBoomIconNameOptionLabel,
  getBoomIconNameValueLabels,
  loadBoomIconNameOptions,
} from "../../Common/BoomIconNames";

const BOOM_ICON_PREFIX = "boom:";
const ASSET_ICON_PREFIX = "asset:";

export type SelectOptionIconOption = {
  label: string;
  value: string;
};

export const getSelectOptionIconValue = (
  type: "boom" | "asset",
  value: string
) => {
  return `${type === "asset" ? ASSET_ICON_PREFIX : BOOM_ICON_PREFIX}${value}`;
};

export const parseSelectOptionIconValue = (value?: string | null) => {
  const iconValue = `${value ?? ""}`;

  if (iconValue.startsWith(ASSET_ICON_PREFIX)) {
    return {
      type: "asset" as const,
      value: iconValue.slice(ASSET_ICON_PREFIX.length),
    };
  }

  if (iconValue.startsWith(BOOM_ICON_PREFIX)) {
    return {
      type: "boom" as const,
      value: iconValue.slice(BOOM_ICON_PREFIX.length),
    };
  }

  return {
    type: "boom" as const,
    value: iconValue,
  };
};

const toBoomOption = (option: SelectOptionIconOption) => ({
  ...option,
  value: getSelectOptionIconValue("boom", option.value),
});

const toAssetOption = (option: SelectOptionIconOption) => ({
  ...option,
  value: getSelectOptionIconValue("asset", option.value),
});

export const loadSelectOptionIconOptions = async (
  search = "",
  prevOptions: SelectOptionIconOption[] = []
) => {
  const boomPrevOptions = prevOptions.filter((option) =>
    option.value.startsWith(BOOM_ICON_PREFIX)
  );
  const assetPrevOptions = prevOptions.filter((option) =>
    option.value.startsWith(ASSET_ICON_PREFIX)
  );
  const boomOptions = loadBoomIconNameOptions(search, boomPrevOptions).options;
  const assetOptions = loadAssetIconOptions(search, assetPrevOptions).options;
  const options = [
    ...boomOptions.map(toBoomOption),
    ...assetOptions.map(toAssetOption),
  ];

  return {
    options,
    hasMore: options.length > 0,
  };
};

export const formatSelectOptionIconOptionLabel = (
  option: SelectOptionIconOption
) => {
  const parsed = parseSelectOptionIconValue(option.value);

  if (parsed.type === "asset") {
    return formatAssetKindOptionLabel({ ...option, value: parsed.value });
  }

  return formatBoomIconNameOptionLabel({ ...option, value: parsed.value });
};

export const getSelectOptionIconValueLabels = async (values?: string[]) => {
  if (!values?.length) {
    return [];
  }

  const labelOptions = await Promise.all(
    values.map(async (value) => {
      const parsed = parseSelectOptionIconValue(value);
      const [labelOption] =
        parsed.type === "asset"
          ? getAssetIconValueLabels([parsed.value]) || []
          : getBoomIconNameValueLabels([parsed.value]) || [];

      return {
        label: labelOption?.label || parsed.value,
        value,
      };
    })
  );

  return labelOptions;
};

export const SelectOptionIcon = ({
  icon,
  width = 18,
  className,
}: {
  icon: string;
  width?: number;
  className?: string;
}) => {
  const parsed = parseSelectOptionIconValue(icon);

  if (!parsed.value) {
    return null;
  }

  if (parsed.type === "asset") {
    return <AssetsIcon assetKind={parsed.value} width={width} />;
  }

  return <BoomIcon icon={parsed.value} width={width} className={className} />;
};
